import {
  createReducer,
  isAnyOf,
} from '@reduxjs/toolkit'

import { CREATOR_TAB } from '@/constants/local-storages'
import { readAllCreators } from '@/redux/creator-actions'
import { startAppListening } from '@/redux/utils'

import {
  activateCreatorTab,
  activateUserTab,
  addCreatorTab,
  addUserTab,
  CreatorTabItem,
  creatorTabNamespace,
  deactivateAllCreatorTabs,
  rearrangeCreatorTabs,
  rearrangeUserTabs,
  removeCreatorTab,
  removeUserTab,
  updateActiveUserTabType,
  updateHomeTabFanUserId,
  updateMassMassageTabVisible,
  UserTabType,
} from './creator-tab-actions'

const getDefaultOpenedTabs = (): CreatorTabItem[] => {
  if (typeof window === 'undefined') return []
  try {
    const openedTabsLocalStorage = window.localStorage.getItem(CREATOR_TAB)
    if (openedTabsLocalStorage) {
      if (!openedTabsLocalStorage.includes('activeUserTabType')) {
        window.localStorage.removeItem(CREATOR_TAB)
        return []
      }
      return JSON.parse(openedTabsLocalStorage) as CreatorTabItem[]
    }
  } catch (e) {
    console.log(e)
  }
  return []
}

const reducer = createReducer(getDefaultOpenedTabs(), (builder) => {
  builder.addCase(activateCreatorTab, (draft, { payload }) => {
    draft.forEach((item) => {
      item.active = payload === item.creatorId
    })
  })

  builder.addCase(removeCreatorTab, (draft, { payload }) => {
    const index = draft.findIndex(({ creatorId }) => payload === creatorId)
    if (index < 0) return
    const { active } = draft[index]
    if (active) {
      if (index < draft.length - 1) {
        draft[index + 1].active = true
      } else if (index > 0) {
        draft[index - 1].active = true
      }
    }
    draft.splice(index, 1)
  })

  builder.addCase(addCreatorTab, (draft, { payload }) => {
    const { creatorId } = payload
    const creatorTabItem = draft.find((item) => creatorId === item.creatorId)
    if (!creatorTabItem) {
      draft.unshift(payload)
    }
  })

  builder.addCase(rearrangeCreatorTabs, (draft, { payload }) => {
    return payload
  })

  builder.addCase(deactivateAllCreatorTabs, (draft) => {
    draft.forEach((item) => {
      item.active = false
    })
  })

  builder.addCase(removeUserTab, (draft, { payload: { userId, creatorId } }) => {
    const creatorTabItem = draft.find((item) => creatorId === item.creatorId)
    if (creatorTabItem) {
      const { userTabItems } = creatorTabItem
      const index = userTabItems.findIndex((item) => item.userId === userId)
      if (index < 0) return
      const { active } = userTabItems[index]
      if (active) {
        if (index < userTabItems.length - 1) {
          userTabItems[index + 1].active = true
        } else if (index > 0) {
          userTabItems[index - 1].active = true
        } else {
          creatorTabItem.activeUserTabType = UserTabType.Home
        }
      }
      userTabItems.splice(index, 1)
    }
  })

  builder.addCase(activateUserTab, (draft, { payload: { userId, creatorId } }) => {
    const creatorTabItem = draft.find((item) => creatorId === item.creatorId)
    if (creatorTabItem) {
      creatorTabItem.userTabItems.forEach((item) => item.active = item.userId === userId)
      creatorTabItem.activeUserTabType = UserTabType.User
    }
  })

  builder.addCase(rearrangeUserTabs, (draft, { payload: { userTabItems, creatorId } }) => {
    const creatorTabItem = draft.find((item) => creatorId === item.creatorId)
    if (creatorTabItem) {
      creatorTabItem.userTabItems = userTabItems
    }
  })

  builder.addCase(
    addUserTab,
    (draft, { payload: { creatorId, userTabItem } }) => {
      if (userTabItem.active) {
        draft.forEach((item) => item.active = false)
      }
      const existingCreatorTabItem = draft.find((item) => creatorId === item.creatorId)
      if (existingCreatorTabItem) {
        if (userTabItem.active) {
          existingCreatorTabItem.userTabItems.forEach((item) => item.active = false)
          existingCreatorTabItem.activeUserTabType = UserTabType.User
          existingCreatorTabItem.active = true
        }
        const existingUserTabIndex = existingCreatorTabItem.userTabItems.findIndex(({ userId }) => userId === userTabItem.userId)
        if (existingUserTabIndex !== -1) {
          existingCreatorTabItem.userTabItems[existingUserTabIndex] = userTabItem
        } else {
          existingCreatorTabItem.userTabItems.unshift(userTabItem)
        }
      } else {
        draft.unshift({
          active: userTabItem.active,
          creatorId,
          activeUserTabType: UserTabType.User,
          userTabItems: [userTabItem],
          massMassageTabVisible: false,
        })
      }
    },
  )

  builder.addCase(updateHomeTabFanUserId, (draft, { payload: { creatorId, fanUserId } }) => {
    const existingCreatorTabItem = draft.find((item) => creatorId === item.creatorId)
    if (existingCreatorTabItem) {
      existingCreatorTabItem.homeTabFanUserId = fanUserId
    }
  })

  builder.addCase(updateActiveUserTabType, (draft, { payload: { creatorId, userTabType } }) => {
    const existingCreatorTabItem = draft.find((item) => creatorId === item.creatorId)
    if (existingCreatorTabItem) {
      existingCreatorTabItem.activeUserTabType = userTabType
      if (userTabType === UserTabType.MassMessage || userTabType === UserTabType.Home) {
        existingCreatorTabItem.userTabItems.forEach((item) => item.active = false)
      }
      if (userTabType === UserTabType.MassMessage) {
        existingCreatorTabItem.massMassageTabVisible = true
      }
    }
  })

  builder.addCase(updateMassMassageTabVisible, (draft, { payload: { creatorId, visible } }) => {
    const existingCreatorTabItem = draft.find((item) => creatorId === item.creatorId)
    if (existingCreatorTabItem) {
      existingCreatorTabItem.massMassageTabVisible = visible
    }
  })
})

startAppListening({
  matcher: isAnyOf(
    readAllCreators.fulfilled,
    activateCreatorTab,
    removeCreatorTab,
    addCreatorTab,
    rearrangeCreatorTabs,
    deactivateAllCreatorTabs,
    removeUserTab,
    activateUserTab,
    rearrangeUserTabs,
    addUserTab,
    updateHomeTabFanUserId,
    updateActiveUserTabType,
  ),
  effect: (_, api) => {
    try {
      const { creatorTab } = api.getState()
      window.localStorage.setItem(CREATOR_TAB, JSON.stringify(creatorTab.map<CreatorTabItem>((item) => ({
        ...item,
        homeTabFanUserId: undefined,
      }))))
    } catch (e) {
      console.error(e)
    }
  },
})

export default { [creatorTabNamespace]: reducer }
